/* eslint pinterest/no-jquery-outside-of-render:0 */
import isEqual from 'lodash/isEqual';
import ValueUtil from 'app/common/lib/ValueUtil';

const contextEvent = {};

/**
 * Struct holding the data necessary to create a context logging object.
 * @param {?PCONST.thrift.EventType} eventType
 * @param {?PCONST.thrift.ViewType} viewType
 * @param {?PCONST.thrift.ViewParameterType} viewParameter
 * @param {?Object} viewData
 * @param {?string} requestIdentifier
 * @param {?PCONST.thrift.ElementType} element
 * @param {?PCONST.thrift.ComponentType} component
 * @param {?string} objectIdStr
 * @param {?number} durationNs
 * @param {?string} pairId
 * @param {?PCONST.thrift.BrowserType} browser
 * @param {?string} clientUUID
 * @param {?string} clientTrackingParams
 * @param {?string} userIdStr
 * @constructor
 */
const ContextEvent = function(
  eventType,
  auxData,
  viewType,
  viewParameter,
  viewData,
  requestIdentifier,
  element,
  component,
  objectIdStr,
  durationNs,
  pairId,
  browser,
  clientUUID,
  clientTrackingParams,
  userIdStr = '',
) {
  /**
   * Event type.
   * @type {?PCONST.thrift.EventType}
   * @private
   */
  this._eventType = eventType;

  /**
   * View type.
   * @type {?PCONST.thrift.ViewType}
   * @private
   */
  this._viewType = viewType;

  /**
   * View parameter.
   * @type {?PCONST.thrift.ViewParameterType}
   * @private
   */
  this._viewParameter = viewParameter;

  /**
   * Helper data for deriving the ViewParameterType from route data.
   * @type {?Object}
   * @private
   */
  this._viewData = viewData;

  /**
   * The request identifier for the first xhr made
   * @type {?string}
   * @private
   */
  this._requestIdentifier = requestIdentifier;

  /**
   * The element that originated the event.
   * @type {?PCONST.thrift.ElementType}
   * @private
   */
  this._element = element;

  /**
   * The component that originated the event.
   * @type {?PCONST.thrift.ComponentType}
   * @private
   */
  this._component = component;

  /**
   * Time of event in nanoseconds.
   * @type {?number}
   * @private
   */
  this._time = null;

  /**
   * The event data.
   * @type {Object}
   */
  this._eventData = {};

  /**
   * The aux data, which currently only works for clicks. On VIEW events, data set here won't propagate to events
   * within the view. Please note that all keys here (and the length of the values) must be whitelisted inside
   * config/event_aux_data_whitelist.py.
   * @type {Object}
   */
  this._auxData = auxData || {};

  /**
   * The objectIdStr for the context event.
   * @type {?string}
   * @private
   */
  this._objectIdStr = objectIdStr;

  /**
   * The duration in nanoseconds.
   * @type {?number}
   * @private
   */
  this._durationNs = durationNs;

  /**
   * The pairId.
   * @type {?string}
   * @private
   */
  this._pairId = pairId;

  /**
   * The browser that the event originated from.
   * @type {?PCONST.thrift.BrowserType}
   * @private
   */
  this._browser = browser;

  /**
   * The clientUUID to send
   * @type {?string}
   * @private
   */
  this._clientUUID = clientUUID;

  /**
   * The clientTrackingParams to be sent with certain pin related events
   * @type {?string}
   * @private
   */
  this._clientTrackingParams = clientTrackingParams;

  /**
   * The userIdStr string version of userId
   * @type {?string}
   * @private
   */
  this._userIdStr = userIdStr;
};

/**
 * Add more impressions to the event. If the event doesn't have any pin
 * impressions then this creates a new list.
 * @param {string} eventTypeName
 * @param {Array.<string>} impressions The list of pin ids that were seen.
 */
ContextEvent.prototype.appendImpressions = function(eventTypeName, impressions) {
  this._eventData[eventTypeName] = this.getImpressions(eventTypeName).concat(impressions);
};

/**
 * Copy the event's context view parameters.
 * @param {!ContextEvent} eventToCopy
 */
ContextEvent.prototype.copyViewContext = function(eventToCopy) {
  this._viewType = eventToCopy.getViewType();
  this._viewParameter = eventToCopy.getViewParameter();
  if (this._viewData) {
    this._viewData = ValueUtil.deepExtend(true, this._viewData, eventToCopy.getViewData());
  } else {
    this._viewData = ValueUtil.deepExtend(true, {}, eventToCopy.getViewData());
  }
};

/**
 * Get the component for the event.
 * @return {PCONST.thrift.ComponentType} componentType
 */
ContextEvent.prototype.getComponent = function() {
  return this._component;
};

/**
 * Get the element for the event.
 * @return {PCONST.thrift.ElementType} elementType
 */
ContextEvent.prototype.getElement = function() {
  return this._element;
};

/**
 * Get the EventType for the event.
 * @return {?PCONST.thrift.EventType}
 */
ContextEvent.prototype.getEventType = function() {
  return this._eventType;
};

/**
 * Get the helper data for the event.
 * @return {?Object}
 */
ContextEvent.prototype.getViewData = function() {
  return this._viewData;
};

/**
 * The impressions for the event.
 * @return {Array.<string>} The impressions.
 */
ContextEvent.prototype.getImpressions = function(eventTypeName) {
  return this._eventData[eventTypeName] || [];
};

/**
 * Get the view parameter for the event.
 * @return {?PCONST.thrift.ViewParameterType}
 */
ContextEvent.prototype.getViewParameter = function() {
  return this._viewParameter;
};

/**
 * Get the ViewType for the event.
 * @return {?PCONST.thrift.ViewType}
 */
ContextEvent.prototype.getViewType = function() {
  return this._viewType;
};

/**
 * Get the event's time.
 * @return {?number} nanoseconds
 */
ContextEvent.prototype.getTime = function() {
  return this._time;
};

/**
 * Get the objectIdStr for the context event.
 * @return {string} objectIdStr
 */
ContextEvent.prototype.getObjectIdStr = function() {
  return this._objectIdStr;
};

/**
 * Get the duration in nanoseconds for the event.
 * @return {?number}
 */
ContextEvent.prototype.getDurationNs = function() {
  return this._durationNs;
};

/**
 * Get the pairId for the event.
 * @return {?string}
 */
ContextEvent.prototype.getPairId = function() {
  return this._pairId;
};

/**
 * Get the browser for the event.
 * @return {?PCONST.thrift.BrowserType}
 */
ContextEvent.prototype.getBrowser = function() {
  return this._browser;
};

/**
 * Get the auxData for the event.
 * @return {?Object} auxData
 */
ContextEvent.prototype.getAuxData = function() {
  return this._auxData;
};

/**
 * Get the client tracking params for the event.
 * @return {?string} auxData
 */
ContextEvent.prototype.getClientTrackingParams = function() {
  return this._clientTrackingParams;
};

/**
 * Whether this event has the same view context as the passed in event.
 * @param {!ContextEvent} otherEvent The event to compare to.
 * @return {boolean} True if the view context is the same.
 */
ContextEvent.prototype.hasSameViewContext = function(otherEvent) {
  return (
    this.getViewType() === otherEvent.getViewType() &&
    this.getViewParameter() === otherEvent.getViewParameter() &&
    isEqual(this.getViewData(), otherEvent.getViewData())
  );
};

/**
 * Set the EventType for the event.
 * @param {?PCONST.thrift.EventType} eventType
 */
ContextEvent.prototype.setEventType = function(eventType) {
  this._eventType = eventType;
};

/**
 * Set the objectIdStr for the context event.
 * @param {string} objectIdStr
 */
ContextEvent.prototype.setObjectIdStr = function(objectIdStr) {
  this._objectIdStr = objectIdStr;
};

/**
 * Set the impressions for the event.
 * @param {Array.<string>} pinImpressions The list of pin ids that were seen.
 */
ContextEvent.prototype.setImpressions = function(type, impressions) {
  this._eventData[type] = impressions;
};

/**
 * Set the networkStoryImpressions for the event.
 * @param {Array.<string>} networkStoryImpressions The list of pin ids that were seen.
 */
ContextEvent.prototype.setNetworkStoryImpressions = function(networkStoryImpressions) {
  this._eventData.networkStoryImpressions = networkStoryImpressions;
};

/**
 * Set the notificationImpressions for the event.
 * @param {Array.<string>} notificationImpressions The list of pin ids that were seen.
 */
ContextEvent.prototype.setNotificationImpressions = function(notificationImpressions) {
  this._eventData.notificationImpressions = notificationImpressions;
};

/**
 * Set the component for the event.
 * @param {PCONST.thrift.ComponentType} componentType
 */
ContextEvent.prototype.setComponent = function(componentType) {
  this._component = componentType;
};

/**
 * Set the element for the event.
 * @param {PCONST.thrift.ElementType} elementType
 */
ContextEvent.prototype.setElement = function(elementType) {
  this._element = elementType;
};

/**
 * Set the auxData for the event.
 * @param {?Object} auxData
 */
ContextEvent.prototype.updateAuxData = function(auxData) {
  this._auxData = { ...this._auxData, ...auxData };
};

/**
 * Set the eventData for the event.
 * @param {?Object} eventData
 */
ContextEvent.prototype.updateEventData = function(eventData) {
  this._eventData = { ...this._eventData, ...eventData };
};

/**
 * Set the request identifier for the event.
 * @param {?string} requestIdentifier
 */
ContextEvent.prototype.setRequestIdentifier = function(requestIdentifier) {
  this._requestIdentifier = requestIdentifier;
};

/**
 * Set the event's time.
 * @param {?number} nanoseconds
 */
ContextEvent.prototype.setTime = function(nanoseconds) {
  this._time = nanoseconds;
};

/**
 * Set the ViewType for the event.
 * @param {?PCONST.thrift.ViewType} viewType
 */
ContextEvent.prototype.setViewType = function(viewType) {
  this._viewType = viewType;
};

/**
 * set the ViewParameter for the event.
 * @param {?PCONST.thrift.ViewParameter} viewParameter
 */
ContextEvent.prototype.setViewParameter = function(viewParameter) {
  this._viewParameter = viewParameter;
};

/**
 * Set the view data for the event.
 * @param {?Object} viewData
 */
ContextEvent.prototype.setViewData = function(viewData) {
  this._viewData = ValueUtil.deepExtend(true, this._viewData, viewData);
};

/**
 * Set the closeup rich types for the event.
 * @param {Array.<number>} closeupRichTypes
 */
ContextEvent.prototype.setCloseupRichTypes = function(closeupRichTypes) {
  if (!this._viewData) {
    this._viewData = {};
  }
  this._viewData.closeup_rich_types = closeupRichTypes;
};

/**
 * Set the toast type for the event.
 * @param {integer} toastType
 */
ContextEvent.prototype.setToastType = function(toastType) {
  if (!this._viewData) {
    this._viewData = {};
  }
  this._viewData.toast_type = toastType;
};

/**
 * Set the duration in nanoseconds for the event.
 * @param {?number}
 */
ContextEvent.prototype.setDurationNs = function(durationNs) {
  this._durationNs = durationNs;
};

/**
 * Set the pairId for the event.
 * @param {?string}
 */
ContextEvent.prototype.setPairId = function(pairId) {
  this._pairId = pairId;
};

/**
 * Set the browser for the event.
 * @param {?PCONST.thrift.BrowserType}
 */
ContextEvent.prototype.setBrowser = function(browser) {
  this._browser = browser;
};

/**
 * Set the clientUUID for the event.
 * @param {?string}
 */
ContextEvent.prototype.setClientUUID = function(clientUUID) {
  this._clientUUID = clientUUID;
};

/**
 * Set the clientTrackingParams for the event.
 * @param {?string}
 */
ContextEvent.prototype.setClientTrackingParams = function(clientTrackingParams) {
  this._clientTrackingParams = clientTrackingParams;
};

/**
 * Get the event in a format ready to send to the server.
 * @return {Object} JSON serializable format where the keys use the python
 *    naming style.
 */
ContextEvent.prototype.toSerializable = function() {
  const event = {
    event_type: this._eventType,
    view_type: this._viewType,
    view_parameter: this._viewParameter,
    view_data: this._viewData,
    request_identifier: this._requestIdentifier,
    element: this._element,
    component: this._component,
    time: this._time,
    event_data: this._eventData,
    aux_data: this._auxData,
    duration_ns: this._durationNs,
    pair_id: this._pairId,
    browser: this._browser,
    clientUUID: this._clientUUID,
    clientTrackingParams: this._clientTrackingParams,
    userIdStr: this._userIdStr,
  };

  if (this._objectIdStr) {
    event[contextEvent.KEY_OBJECT_ID_STR] = this._objectIdStr;
  }

  return event;
};

/**
 * The event type key for context events.
 * @type {string}
 */
contextEvent.KEY_EVENT_TYPE = 'event_type';

/**
 * The object id string key for context events.
 * @type {string}
 */
contextEvent.KEY_OBJECT_ID_STR = 'object_id_str';

/**
 * Create a ContextEvent from an event type
 * @param {PCONST.thrift.EventType} eventType
 * @return {!ContextEvent}
 */
contextEvent.fromEventType = function(eventType) {
  const event = contextEvent.fromPlainObject({});
  event.setEventType(eventType);
  return event;
};

/**
 * Create a ContextEvent from a plain object.
 * @param {Object} event Plain object for an event. The object keys should be
 *     in python format.
 * @return {!ContextEvent}
 */
contextEvent.fromPlainObject = function(event) {
  return new ContextEvent(
    event.event_type,
    event.aux_data,
    event.view_type,
    event.view_parameter,
    event.view_data,
    event.request_identifier,
    event.element,
    event.component,
    event.object_id_str,
    event.duration_ns,
    event.pair_id,
    event.browser,
    event.clientUUID,
    event.clientTrackingParams,
    event.userIdStr,
  );
};

contextEvent.clone = function(eventObj) {
  return contextEvent.fromPlainObject(eventObj.toSerializable());
};

export default contextEvent;
